import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { apiService, ApiError } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { UserNote } from '../../types/api';
import { Loader2, MessageSquare, Pencil, X, Check, ChevronLeft, ChevronRight, Bot } from 'lucide-react';

const NOTES_PER_PAGE = 20;

interface UserNotesProps {
  userId: string;
}

function formatNoteTimestamp(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

function AuthorBadge({ authorType }: { authorType: string }) {
  const config: Record<string, { label: string; className: string }> = {
    system: {
      label: 'System',
      className: 'bg-slate-100 text-slate-600 border-slate-200',
    },
    admin: {
      label: 'Admin',
      className: 'bg-violet-50 text-violet-700 border-violet-200',
    },
    manager: {
      label: 'Manager',
      className: 'bg-blue-50 text-blue-700 border-blue-200',
    },
  };

  const { label, className } = config[authorType] || config.manager;

  return (
    <span className={`inline-flex items-center px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider rounded border ${className}`}>
      {label}
    </span>
  );
}

export function UserNotes({ userId }: UserNotesProps) {
  const { manager } = useAuth();

  const [notes, setNotes] = useState<UserNote[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const [newNoteContent, setNewNoteContent] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const [editingNoteId, setEditingNoteId] = useState<string | null>(null);
  const [editContent, setEditContent] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);

  const hasFetched = useRef(false);

  const fetchNotes = useCallback(async (offset: number = 0) => {
    try {
      setIsLoading(true);
      setError('');
      const response = await apiService.getUserNotes(userId, NOTES_PER_PAGE, offset);
      setNotes(response.data);
      setTotal(response.total);
    } catch (err) {
      const message = err instanceof ApiError ? err.message : 'Failed to load notes';
      setError(message);
    } finally {
      setIsLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;
    fetchNotes(0);
  }, [fetchNotes]);

  const handlePageChange = useCallback((newPage: number) => {
    setPage(newPage);
    fetchNotes(newPage * NOTES_PER_PAGE);
  }, [fetchNotes]);

  const handleCreateNote = useCallback(async () => {
    const trimmed = newNoteContent.trim();
    if (!trimmed) return;

    try {
      setIsSaving(true);
      setError('');
      await apiService.createUserNote(userId, trimmed);
      setNewNoteContent('');
      setPage(0);
      await fetchNotes(0);
    } catch (err) {
      const message = err instanceof ApiError ? err.message : 'Failed to save note';
      setError(message);
    } finally {
      setIsSaving(false);
    }
  }, [userId, newNoteContent, fetchNotes]);

  const handleStartEdit = useCallback((note: UserNote) => {
    setEditingNoteId(note.id);
    setEditContent(note.content);
  }, []);

  const handleCancelEdit = useCallback(() => {
    setEditingNoteId(null);
    setEditContent('');
  }, []);

  const handleSaveEdit = useCallback(async () => {
    if (!editingNoteId) return;
    const trimmed = editContent.trim();
    if (!trimmed) return;

    try {
      setIsUpdating(true);
      setError('');
      const response = await apiService.updateUserNote(editingNoteId, trimmed);
      setNotes(prev => prev.map(n => n.id === editingNoteId ? response.data : n));
      setEditingNoteId(null);
      setEditContent('');
    } catch (err) {
      const message = err instanceof ApiError ? err.message : 'Failed to update note';
      setError(message);
    } finally {
      setIsUpdating(false);
    }
  }, [editingNoteId, editContent]);

  const canEditNote = useCallback((note: UserNote): boolean => {
    if (note.authorType === 'system') return false;
    if (!manager) return false;
    return note.authorId === manager.id;
  }, [manager]);

  const totalPages = Math.ceil(total / NOTES_PER_PAGE);

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-6">
      <h2 className="text-xl font-semibold text-gray-900 mb-4">Notes & Handoffs</h2>

      {/* Error */}
      {error && (
        <div className="p-3 text-sm text-red-600 bg-red-50 border border-red-200 rounded-md mb-4">
          {error}
        </div>
      )}

      {/* Notes List */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : notes.length === 0 ? (
        <div className="text-center py-6">
          <MessageSquare className="h-10 w-10 text-gray-300 mx-auto mb-3" />
          <p className="text-sm text-muted-foreground">No notes yet.</p>
          <p className="text-xs text-muted-foreground mt-1">Add the first note below.</p>
        </div>
      ) : (
        <div className="space-y-3 mb-5">
          <p className="text-xs text-muted-foreground">{total} total</p>

          {notes.map((note) => {
            const isSystem = note.authorType === 'system';
            const isEditing = editingNoteId === note.id;
            const editable = canEditNote(note);

            return (
              <Card
                key={note.id}
                className={`transition-colors ${isSystem ? 'bg-slate-50 border-slate-200' : 'bg-white'}`}
              >
                <CardContent className="py-3 px-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2 min-w-0">
                      {isSystem && <Bot className="h-3.5 w-3.5 text-slate-500 flex-shrink-0" />}
                      <span className="text-sm font-medium text-gray-900 truncate">
                        {note.authorName}
                      </span>
                      <AuthorBadge authorType={note.authorType} />
                      <span className="text-xs text-muted-foreground flex-shrink-0">
                        {formatNoteTimestamp(note.createdAt)}
                      </span>
                      {note.updatedAt !== note.createdAt && (
                        <span className="text-[10px] text-muted-foreground italic flex-shrink-0">(edited)</span>
                      )}
                    </div>
                    {editable && !isEditing && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 w-7 p-0 text-muted-foreground hover:text-foreground"
                        onClick={() => handleStartEdit(note)}
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                    )}
                  </div>

                  {isEditing ? (
                    <div className="space-y-2">
                      <Textarea
                        value={editContent}
                        onChange={(e) => setEditContent(e.target.value)}
                        className="min-h-[60px] resize-y text-sm"
                        disabled={isUpdating}
                        autoFocus
                      />
                      <div className="flex items-center gap-2 justify-end">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={handleCancelEdit}
                          disabled={isUpdating}
                        >
                          <X className="h-3.5 w-3.5 mr-1" />
                          Cancel
                        </Button>
                        <Button
                          size="sm"
                          onClick={handleSaveEdit}
                          disabled={!editContent.trim() || isUpdating}
                        >
                          {isUpdating ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />
                          ) : (
                            <Check className="h-3.5 w-3.5 mr-1" />
                          )}
                          Save
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <p className={`text-sm whitespace-pre-wrap break-words ${isSystem ? 'text-slate-600' : 'text-gray-700'}`}>
                      {note.content}
                    </p>
                  )}
                </CardContent>
              </Card>
            );
          })}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between pt-2">
              <p className="text-xs text-muted-foreground">
                Page {page + 1} of {totalPages}
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(page - 1)}
                  disabled={page === 0 || isLoading}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(page + 1)}
                  disabled={page >= totalPages - 1 || isLoading}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Add Note */}
      <div className="border-t border-gray-200 pt-4">
        <h4 className="text-sm font-semibold text-gray-900 mb-3">Add Note</h4>
        <Textarea
          value={newNoteContent}
          onChange={(e) => setNewNoteContent(e.target.value)}
          placeholder="Log a note about client status, waiting for documents, dispute updates, etc..."
          className="min-h-[80px] resize-y text-sm"
          disabled={isSaving}
        />
        <div className="flex justify-end mt-3">
          <Button
            onClick={handleCreateNote}
            disabled={!newNoteContent.trim() || isSaving}
            size="sm"
          >
            {isSaving ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <MessageSquare className="h-4 w-4 mr-2" />
            )}
            Save Note
          </Button>
        </div>
      </div>
    </div>
  );
}
